type JWTPayload = Record<string, unknown>;

const decoder = new TextDecoder();

// Single-entry memo. Every accessor decodes the payload independently and the
// login flow reads several claims off the same token in a row, so caching the
// last decode removes the repeated base64 + JSON work.
let memoizedJwt: string | undefined;
let memoizedPayload: JWTPayload | undefined;

export function isJWTExpired(jwt?: string): boolean {
  const expDate = getJWTExpDate(jwt);
  // No usable `exp` means expiry cannot be established — fail closed.
  return expDate ? expDate.getTime() - Date.now() < 10_000 : true;
}

export function getJWTExpDate(jwt?: string): Date | undefined {
  return getDateClaim(jwt, 'exp');
}

export function getJWTIatDate(jwt?: string): Date | undefined {
  return getDateClaim(jwt, 'iat');
}

export function getAuthTime(jwt?: string): Date | undefined {
  return getDateClaim(jwt, 'auth_time');
}

export function getUserId(jwt?: string): string | null {
  return getStringClaim(jwt, 'sub') ?? null;
}

export function getSessionId(jwt?: string): string | undefined {
  return getStringClaim(jwt, 'sid');
}

export function getTipicoCustomerId(jwt?: string): string | undefined {
  return getStringClaim(jwt, 'tipicoCustomerId');
}

/** Reads a NumericDate claim (seconds since the epoch) as a `Date`. */
function getDateClaim(jwt: string | undefined, claim: string): Date | undefined {
  const seconds = getJWTPayload(jwt)?.[claim];
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return undefined;

  const date = new Date(seconds * 1000);
  return Number.isFinite(date.getTime()) ? date : undefined;
}

function getStringClaim(jwt: string | undefined, claim: string): string | undefined {
  const value = getJWTPayload(jwt)?.[claim];
  return typeof value === 'string' ? value : undefined;
}

function getJWTPayload(jwt?: string): JWTPayload | undefined {
  if (!jwt) return undefined;
  if (jwt === memoizedJwt) return memoizedPayload;

  memoizedJwt = jwt;
  memoizedPayload = decodeJWTPayload(jwt);
  return memoizedPayload;
}

function decodeJWTPayload(jwt: string): JWTPayload | undefined {
  const segments = jwt.split('.');
  const segment = segments[1];
  if (!segment) {
    window.$app.logger.log(`jwt: no payload segment (${segments.length} segments)`);
    return undefined;
  }

  // JWT segments use the base64url alphabet and omit padding, while atob()
  // accepts standard, padded base64.
  const base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
  const remainder = base64.length % 4;
  // Base64 encoding never produces a length of 4n+1, so no padding can fix it.
  if (remainder === 1) {
    window.$app.logger.log(`jwt: payload segment length ${segment.length} is not valid base64`);
    return undefined;
  }

  try {
    const binary = window.atob(remainder === 0 ? base64 : base64.padEnd(base64.length + 4 - remainder, '='));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const payload: unknown = JSON.parse(decoder.decode(bytes));
    if (typeof payload !== 'object' || payload === null) {
      window.$app.logger.log(`jwt: payload decoded to ${typeof payload}, expected an object`);
      return undefined;
    }
    return payload as JWTPayload;
  } catch (err) {
    window.$app.logger.log(`jwt: decoding payload segment (${segment.length} chars) failed:`, err);
    return undefined;
  }
}
